import React, { ReactElement, useEffect, useState } from 'react';
import {
  Col,
  Label,
  Loader,
  Row,
  usePageNumberFromUrl,
  usePerPageValueFromUrl,
  useSortValueFromUrl,
} from 'idsk-react-components';
import { MobileFilterModal, Pagination, useResponsiveContext } from 'slovlex-idsk-react-components';
import { useSearchParams } from 'react-router-dom';
import NavrhAktuEuListComponent from './component/NavrhAktuEuListComponent';
import getUpdatedSearchParams from './function/getUpdatedSearchParams';
import NavrhAktuEuFilter from '../../model/NavrhAktuEuFilter';
import { SafeParameterSetter } from '../../types/SafeParameterSetter';
import FilterTagsComponent from './component/FilterTagsComponent';
import getPaginationTotalLabel from '../../function/getPaginationTotalLabel';
import countActiveFilters from '../../function/countActiveFilters';
import SidePanel from './component/SidePanel';
import { defaultPerPageValue, perPageDataArray } from '../../constants/PaginationPerPageConstants';
import useAktEuFilterList from './hooks/useAktEuFilterList';
import emptyFilter from './emptyFilter';
import NavrhAktuEuListSort from './component/NavrhAktuEuListSort';
import SearchInput from './component/SearchInput';
import MobileFilters from './component/MobileFilters';

interface Props {
  initialFilter: NavrhAktuEuFilter;
}

// datum prijatia aktu od/do je pre pouzivatela jedno kriterium - jeden panel
// v bocnom filtri a jeden filter tag
const criteriaGroups: Array<Array<keyof NavrhAktuEuFilter>> = [
  ['datumPrijatiaAktuOd', 'datumPrijatiaAktuDo'],
];

const NavrhAktuEuList = ({
  initialFilter,
}: Props): ReactElement => {
  const [firstLoad, setFirstLoad] = useState(true);
  const { page } = usePageNumberFromUrl('page');
  const { perPageValue } = usePerPageValueFromUrl('perPage', perPageDataArray, defaultPerPageValue);
  const [searchParams, setSearchParams] = useSearchParams();
  const { sort } = useSortValueFromUrl();
  const [filter, setFilter] = useState<NavrhAktuEuFilter>(initialFilter);
  const [filterCopy, setFilterCopy] = useState<NavrhAktuEuFilter>(filter);
  const { isSmallScreen } = useResponsiveContext();
  const [isOpenModal, setIsOpenModal] = useState(false);

  const {
    aktEuPage,
    requestStatus,
  } = useAktEuFilterList(filter);

  const onUpdateField: SafeParameterSetter<NavrhAktuEuFilter> = (fieldName, value): void => {
    setFilter({
      ...filterCopy,
      [fieldName]: value,
    });
    setFilterCopy({
      ...filterCopy,
      [fieldName]: value,
    });
  };

  const onUpdateFieldCopy: SafeParameterSetter<NavrhAktuEuFilter> = (fieldName, value): void => {
    setFilterCopy(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const onRecordsPerPageChange = (value: number): void => {
    const updatedSearchParams = new URLSearchParams(searchParams.toString());
    updatedSearchParams.set('perPage', value.toString());
    // SLASLX-3400: replace, not push. setSearchParams default pushes a new
    // history entry per URL update — combined with the filter auto-sync
    // useEffect below, that makes browser Back retrace every filter tweak
    // instead of leaving the page in one click.
    setSearchParams(updatedSearchParams.toString(), { replace: true });
  };

  useEffect(() => {
    if (Array.from(searchParams.values()).length === 0
      && JSON.stringify(filter) !== JSON.stringify(emptyFilter)) {
      setFilter(emptyFilter);
      setFilterCopy(emptyFilter);
      setFirstLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!firstLoad) {
      const urlParamsFromFilter = getUpdatedSearchParams(1, perPageValue, sort, filter);
      // SLASLX-3400: replace — the URL is a reflection of state, not a user
      // navigation; pushing here breaks the 1-Back-leaves-the-page contract.
      setSearchParams(urlParamsFromFilter, { replace: true });
    }
    setFirstLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filter)]);

  const handleCloseModal = ():void => {
    setIsOpenModal(false);
    window.parent.postMessage({
      type: 'iframe-to-parent-filter-modal-close',
    }, '*');
    setFilter(filterCopy);
  };

  const handleOpenModal = ():void => {
    setIsOpenModal(true);
    window.parent.postMessage({
      type: 'iframe-to-parent-filter-modal-open',
    }, '*');
    setFilter(filterCopy);
  };

  const clearFilters = (): void => {
    setFilter(emptyFilter);
    setFilterCopy(emptyFilter);
  };

  // tlacidlo na vymazanie filtra sa podla zadania zobrazuje az pri dvoch a viac kriteriach
  const onClearFilters = countActiveFilters(filter, criteriaGroups) >= 2 ? clearFilters : null;

  return (
    <div className="akt-eu-list-component">
      {isSmallScreen && (
        <SearchInput
          filterCopy={filterCopy}
          label="Hľadaný výraz"
          filterSearchText={filterCopy?.hladanyVyraz}
          fieldName="hladanyVyraz"
          onUpdateFieldCopy={onUpdateFieldCopy}
          setFilter={setFilter}
          className="slovlex-idsk-side-panel-input-wrapper first-container"
        />
      )}
      <MobileFilterModal
        handleClose={handleCloseModal}
        handleOpen={handleOpenModal}
        isOpen={isOpenModal}
        numberOfResults={aktEuPage?.totalElements || 0}
        filters={(
          <MobileFilters
            setFilter={setFilter}
            filterCopy={filterCopy}
            onUpdateFieldCopy={onUpdateFieldCopy}
            filter={filter}
            onUpdateField={onUpdateField}
          />
          )}
        perPageDataArray={perPageDataArray}
        onPerPageChange={onRecordsPerPageChange}
        perPageValue={perPageValue}
        clearFilters={onClearFilters}
        filterTags={(
          <FilterTagsComponent
            filter={filter}
            onUpdateField={onUpdateField}
          />
          )}
      />
      {!isOpenModal && (
        <Row className="akt-eu-list-component__results-row">
          {!isSmallScreen && (
          <Col className="side-panel-column" width="one-quarter">
            <SidePanel
              setFilter={setFilter}
              filterCopy={filterCopy}
              onUpdateFieldCopy={onUpdateFieldCopy}
              filter={filter}
              onUpdateField={onUpdateField}
            />
          </Col>
          )}
          <Col width="three-quarters">
            <FilterTagsComponent
              filter={filter}
              onUpdateField={onUpdateField}
              onClearFilters={onClearFilters}
            />
            <Loader loading={requestStatus.loading}>
              <>
                {isSmallScreen && (
                  <Row className="akt-eu-mobile-sort-select-row">
                    <Col>
                      <NavrhAktuEuListSort />
                    </Col>
                  </Row>
                )}
                <Row className="pagination-label-sort-row">
                  <Col
                    width="full"
                    widthDesktop="one-third"
                    className={isSmallScreen && 'pagination-label-mobile'}
                  >
                    <Label>{getPaginationTotalLabel(aktEuPage, page)}</Label>
                  </Col>
                  {!isSmallScreen && (
                    <Col width="full" widthDesktop="two-thirds">
                      <NavrhAktuEuListSort />
                    </Col>
                  )}
                </Row>
                {aktEuPage && (
                  <div className="akt-eu-list">
                    <NavrhAktuEuListComponent
                      isSelectedFilter={JSON.stringify(filter) !== JSON.stringify(emptyFilter)}
                      aktEuPage={aktEuPage}
                    />
                    <Row className="akt-eu-list-pagination-row">
                      <Col>
                        <Pagination
                          recordsPerPage={perPageDataArray}
                          hideTotal
                          number={page}
                          size={aktEuPage.size}
                          totalElements={aktEuPage.totalElements}
                          perPageValue={perPageValue}
                          onRecordsPerPageChange={(value) => {
                            const updatedSearchParams = new URLSearchParams(searchParams.toString());
                            updatedSearchParams.set('perPage', value.toString());
                            setSearchParams(updatedSearchParams.toString(), { replace: true });
                          }}
                        />
                      </Col>
                    </Row>
                  </div>
                )}
              </>
            </Loader>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default NavrhAktuEuList;
